/**
 * SharingSection - Shared user and group selection
 * Used by both FAQ and Document dialogs
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { RecipientPicker } from './RecipientPicker';
import { RecipientSelection, RecipientUtils } from './RecipientPicker/types';

interface SharingSectionProps {
  /** Current user IDs (comma-separated string) */
  userIds?: string;
  /** Current group IDs (comma-separated string) */
  groupIds?: string;
  /** Callback when selection changes */
  onChange?: (userIds: string, groupIds: string) => void;
  /** Custom title for the section */
  title?: string;
  /** Custom helper text */
  helperText?: string;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Error state */
  error?: boolean;
}

export const SharingSection: React.FC<SharingSectionProps> = ({
  userIds = '',
  groupIds = '',
  onChange,
  title,
  helperText,
  disabled = false,
  error = false
}) => {
  const { t } = useTranslation();
  const selection = RecipientUtils.fromLegacy(userIds, groupIds);

  const handleChange = (nextSelection: RecipientSelection) => {
    if (!onChange) {
      return;
    }

    const legacy = RecipientUtils.toLegacy(nextSelection);
    onChange(legacy.userIds, legacy.groupIds);
  };

  return (
    <Box>
      {title && (
        <Typography variant="h6" sx={{ mb: 2 }}>
          {title}
        </Typography>
      )}

      <RecipientPicker
        variant="side-by-side"
        value={selection}
        onChange={handleChange}
        label={t('documents.sharing.shareWith', 'Teilen mit')}
        helperText={helperText || t('documents.sharing.recipientsHelp', 'Wählen Sie Benutzer und Gruppen aus, mit denen Sie diesen Inhalt teilen möchten')}
        disabled={disabled}
        error={error}
      />
    </Box>
  );
};

export default SharingSection;
